import { type FC, useState, type JSX } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import InfoCircleFilled from 'dibs-icons/exports/legacy/InfoCircleFilled';

import { estimateInfo } from 'dibs-pricing-guidance/exports/pricingGuidanceMessages';
import { trackPricingGuidanceModuleTooltipView } from 'dibs-pricing-guidance/exports/pricingGuidanceTracking';

import { type PricingGuidanceInfo_item$key } from './__generated__/PricingGuidanceInfo_item.graphql';

type Props = {
    item: PricingGuidanceInfo_item$key;
    iconClassName?: string;
    trigger?: 'siderail';
    InfoIcon?: FC<{ variation: $TSFixMe }>;
};

export const PricingGuidanceInfo: FC<Props> = ({
    item: itemRef,
    iconClassName,
    trigger,
    InfoIcon,
}) => {
    const item = useFragment(
        graphql`
            fragment PricingGuidanceInfo_item on Item {
                ...pricingGuidanceTracking_item
            }
        `,
        itemRef
    );

    const [showTooltip, setShowTooltip] = useState(false);

    const onShowTooltip = (): void => {
        if (!showTooltip) {
            setShowTooltip(true);
            trackPricingGuidanceModuleTooltipView({
                item,
                trigger,
            });
        }
    };

    const getIcon = (): JSX.Element => {
        if (InfoIcon) {
            if (showTooltip) {
                return <InfoIcon variation="20Filled" />;
            }

            return <InfoIcon variation="20" />;
        }

        const iconClasses = classnames(dibsCss.h14px, dibsCss.w14px, iconClassName);

        if (showTooltip) {
            return <InfoCircleFilled className={iconClasses} />;
        }

        return <InfoCircle className={iconClasses} />;
    };

    return (
        <div
            onMouseEnter={onShowTooltip}
            onMouseLeave={() => setShowTooltip(false)}
            className={classnames(dibsCss.relative, dibsCss.flex, dibsCss.itemsCenter)}
        >
            <div className={classnames(dibsCss.cursorPointer, dibsCss.flex, dibsCss.itemsCenter)}>
                {getIcon()}
            </div>
            <Tooltip
                isVisible={showTooltip}
                direction={trigger === 'siderail' ? 'top' : 'right'}
                zIndex="low"
            >
                <div className={dibsCss.whitespaceNormal} id="pdp.pdpRespPricingGuidance.tooltip">
                    {estimateInfo}
                </div>
            </Tooltip>
        </div>
    );
};
